<template>
  <v-btn
    color="primary"
    outlined
    :small="small"
    :loading="loading"
    @click="generatePDF"
    :disabled="!data"
    class="export-pdf-btn"
  >
    <v-icon left small>mdi-file-pdf-box</v-icon>
    {{ text || 'PDF' }}
  </v-btn>
</template>

<script>
export default {
  name: 'ExportPDF',
  props: {
    data: { type: Object, required: true },
    filename: { type: String, default: 'fiche_vente' },
    text: { type: String, default: 'PDF' },
    color: { type: String, default: 'primary' },
    outlined: { type: Boolean, default: true },
    small: { type: Boolean, default: false },
    isReturn: { type: Boolean, default: false }
  },
  data() {
    return { loading: false }
  },
  methods: {
    // Format: 1 000.00
    fmt(value) {
      if (value === null || value === undefined || isNaN(value)) return '0.00'
      const num = parseFloat(value)
      const parts = num.toFixed(2).split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      return parts.join('.')
    },

    fmtDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString('fr-FR')
    },

    fmtTime(d) {
      if (!d) return ''
      return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    },

    getBusiness() {
      try { return JSON.parse(localStorage.business) || {} }
      catch { return {} }
    },

    async generatePDF() {
      if (!this.data) return
      this.loading = true

      try {
        const { jsPDF } = await import('jspdf')
        const doc = new jsPDF('p', 'mm', 'a4')
        const sale = this.data
        const cur = sale.rate ? sale.rate.name : 'HTG'
        const biz = this.getBusiness()

        // Layout constants
        const ml = 16
        const mr = 194
        const cw = mr - ml  // 178
        let y = 0

        // Color palette
        const headerColor = this.isReturn ? [245, 132, 59] : [25, 65, 115]
        const C = {
          primary: headerColor,
          dark: [30, 41, 59],
          text: [51, 65, 85],
          muted: [148, 163, 184],
          light: [241, 245, 249],
          border: [226, 232, 240],
          red: [220, 38, 38],
          green: [22, 163, 74],
        }

        // Helper: draw a horizontal line
        const hline = (yy, color, width) => {
          doc.setDrawColor(...(color || C.border))
          doc.setLineWidth(width || 0.2)
          doc.line(ml, yy, mr, yy)
        }

        // ========================================
        // TOP HEADER BAR
        // ========================================
        // Dark blue header band
        doc.setFillColor(...C.primary)
        doc.rect(0, 0, 210, 32, 'F')

        // Business name (large, white)
        doc.setFontSize(16)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(255, 255, 255)
        doc.text(biz.name || 'KES LAKAY', ml, 14)

        // Store info line (white, smaller)
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(200, 215, 235)
        const storeInfo = []
        if (sale.store && sale.store.name) storeInfo.push(sale.store.name)
        if (sale.store && sale.store.phone) storeInfo.push(sale.store.phone)
        if (sale.store && sale.store.address) storeInfo.push(sale.store.address)
        if (storeInfo.length > 0) {
          doc.text(storeInfo.join('  •  '), ml, 21)
        }

        // Receipt number on the right
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(255, 255, 255)
        doc.text(`#${sale.sale_number}`, mr, 14, { align: 'right' })

        // Date on the right
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(200, 215, 235)
        doc.text(`${this.fmtDate(sale.created_at)} à ${this.fmtTime(sale.created_at)}`, mr, 21, { align: 'right' })

        // Document type label
        doc.setFontSize(7)
        doc.setTextColor(160, 185, 215)
        doc.text(this.isReturn ? 'FICHE DE RETOUR' : 'FICHE DE VENTE', ml, 28)

        y = 40

        // ========================================
        // CANCELLATION BANNER (if applicable)
        // ========================================
        if (sale.status == 0) {
          doc.setFillColor(254, 226, 226)
          doc.setDrawColor(252, 165, 165)
          doc.setLineWidth(0.3)
          doc.roundedRect(ml, y - 3, cw, 12, 1.5, 1.5, 'FD')

          doc.setFontSize(8.5)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(...C.red)
          doc.text('VENTE ANNULÉE', ml + 4, y + 2)

          doc.setFont('helvetica', 'normal')
          doc.setFontSize(7.5)
          doc.setTextColor(127, 29, 29)
          let info = ''
          if (sale.canceled_at) info += this.fmtDate(sale.canceled_at) + ' '
          if (sale.cancellation_user) info += '— ' + sale.cancellation_user.full_name + ' '
          if (sale.cancellation_reason) info += '— ' + sale.cancellation_reason
          doc.text(info.trim(), ml + 40, y + 2)

          y += 16
        }

        // ========================================
        // INFO GRID — 4 boxes
        // ========================================
        const boxW = cw / 4
        const boxH = 16
        const infoBoxes = [
          { label: 'CLIENT', value: sale.customer ? sale.customer.full_name : '—' },
          { label: 'AGENT', value: sale.user ? sale.user.full_name : '—' },
          { label: 'TAUX DU JOUR', value: this.fmt(sale.daily_rate) },
          { label: 'STATUT', value: sale.status == 1 ? 'Actif' : 'Annulé' },
        ]

        infoBoxes.forEach((box, i) => {
          const bx = ml + (i * boxW)

          // Light background
          doc.setFillColor(...C.light)
          doc.setDrawColor(...C.border)
          doc.setLineWidth(0.15)
          doc.roundedRect(bx + 1, y, boxW - 2, boxH, 1.5, 1.5, 'FD')

          // Label
          doc.setFontSize(6)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(...C.muted)
          doc.text(box.label, bx + 4, y + 5)

          // Value
          doc.setFontSize(8.5)
          doc.setFont('helvetica', 'bold')
          if (box.value === 'Annulé') {
            doc.setTextColor(...C.red)
          } else {
            doc.setTextColor(...C.dark)
          }
          // Truncate if too long
          let val = box.value
          if (val.length > 18) val = val.substring(0, 16) + '..'
          doc.text(val, bx + 4, y + 12)
        })

        y += boxH + 8

        // ========================================
        // PRODUCTS TABLE
        // ========================================
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(...C.dark)
        doc.text('Produits', ml, y)

        const prodCount = sale.sale_products ? sale.sale_products.length : 0
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(...C.muted)
        doc.text(`${prodCount} article${prodCount > 1 ? 's' : ''}`, mr, y, { align: 'right' })

        y += 5

        // Column definitions: [x_start, width, label, align]
        const colProduct = ml + 2
        const colQty = ml + 100
        const colPrice = ml + 125
        const colTotal = mr - 2

        // Table header
        doc.setFillColor(...C.primary)
        doc.roundedRect(ml, y, cw, 7, 1, 1, 'F')

        doc.setFontSize(6.5)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(255, 255, 255)
        doc.text('PRODUIT', colProduct, y + 4.5)
        doc.text('QTÉ', colQty + 8, y + 4.5, { align: 'center' })
        doc.text('PRIX UNIT.', colPrice + 12, y + 4.5, { align: 'center' })
        doc.text('TOTAL', colTotal, y + 4.5, { align: 'right' })
        y += 7

        // Product rows
        const rowH = 7.5
        if (sale.sale_products) {
          sale.sale_products.forEach((p, idx) => {
            if (y > 258) { doc.addPage(); y = 16 }

            // Alternating bg
            if (idx % 2 === 0) {
              doc.setFillColor(250, 251, 252)
              doc.rect(ml, y, cw, rowH, 'F')
            }

            doc.setFontSize(8)
            doc.setFont('helvetica', 'normal')
            doc.setTextColor(...C.text)

            let name = p.product ? p.product.name : '—'
            if (name.length > 45) name = name.substring(0, 42) + '...'
            doc.text(name, colProduct, y + 5)

            doc.setTextColor(...C.dark)
            doc.text(String(p.quantity), colQty + 8, y + 5, { align: 'center' })
            doc.text(this.fmt(p.price), colPrice + 12, y + 5, { align: 'center' })

            doc.setFont('helvetica', 'bold')
            doc.text(this.fmt(p.total), colTotal, y + 5, { align: 'right' })

            // Row bottom border
            hline(y + rowH, C.border, 0.1)
            y += rowH
          })
        }

        // ========================================
        // TOTALS (right-aligned block)
        // ========================================
        y += 4
        const totLabelX = ml + cw * 0.55
        const totValX = mr - 2

        // Subtotal
        doc.setFontSize(8.5)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(...C.muted)
        doc.text('Sous-total', totLabelX, y)
        doc.setTextColor(...C.dark)
        doc.text(`${this.fmt(sale.subtotal)} ${cur}`, totValX, y, { align: 'right' })
        y += 5.5

        // Discount
        if (sale.discount > 0) {
          doc.setTextColor(...C.red)
          doc.text(`Réduction (${sale.discount}%)`, totLabelX, y)
          doc.text(`-${this.fmt(sale.subtotal * sale.discount / 100)} ${cur}`, totValX, y, { align: 'right' })
          y += 5.5
        }

        // Tax
        if (sale.taxe > 0) {
          doc.setTextColor(...C.muted)
          doc.text('Taxe', totLabelX, y)
          doc.setTextColor(...C.dark)
          doc.text(`${this.fmt(sale.taxe)}%`, totValX, y, { align: 'right' })
          y += 5.5
        }

        // Service fee
        if (sale.service_fee > 0) {
          doc.setTextColor(...C.muted)
          doc.text('Frais de service', totLabelX, y)
          doc.setTextColor(...C.dark)
          doc.text(`${this.fmt(sale.service_fee)} ${cur}`, totValX, y, { align: 'right' })
          y += 5.5
        }

        // Grand total with blue background
        y += 1
        doc.setFillColor(...C.primary)
        doc.roundedRect(totLabelX - 4, y - 4, mr - totLabelX + 6, 10, 1.5, 1.5, 'F')

        doc.setFontSize(11)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(255, 255, 255)
        doc.text('TOTAL', totLabelX, y + 3)
        doc.text(`${this.fmt(sale.total)} ${cur}`, totValX, y + 3, { align: 'right' })
        y += 14

        // ========================================
        // PAYMENTS
        // ========================================
        if (sale.payments && sale.payments.length > 0) {
          if (y > 250) { doc.addPage(); y = 16 }

          doc.setFontSize(10)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(...C.dark)
          doc.text('Paiements', ml, y)
          y += 5

          // Header
          doc.setFillColor(...C.light)
          doc.rect(ml, y, cw, 6.5, 'F')
          hline(y + 6.5, C.border, 0.15)

          doc.setFontSize(6.5)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(...C.muted)
          doc.text('MÉTHODE', ml + 2, y + 4.2)
          doc.text('MONTANT', mr - 2, y + 4.2, { align: 'right' })
          y += 6.5

          // Rows
          sale.payments.forEach(payment => {
            doc.setFontSize(8.5)
            doc.setFont('helvetica', 'normal')
            doc.setTextColor(...C.text)
            doc.text(payment.method ? payment.method.name : '—', ml + 2, y + 5)

            doc.setFont('helvetica', 'bold')
            doc.setTextColor(...C.green)
            const pcur = payment.rate_id == 2 ? 'USD' : 'HTG'
            doc.text(`${this.fmt(payment.amount)} ${pcur}`, mr - 2, y + 5, { align: 'right' })

            hline(y + rowH, C.border, 0.1)
            y += rowH
          })

          // Change
          if (sale.change > 0) {
            doc.setFont('helvetica', 'normal')
            doc.setTextColor(...C.muted)
            doc.text('Monnaie rendue', ml + 2, y + 5)
            doc.setFont('helvetica', 'bold')
            const chgCur = sale.change_rate_id == 2 ? 'USD' : 'HTG'
            doc.text(`${this.fmt(sale.change)} ${chgCur}`, mr - 2, y + 5, { align: 'right' })
            y += rowH
          }
        }

        // ========================================
        // NOTE (if any)
        // ========================================
        if (sale.memo) {
          y += 6
          if (y > 260) { doc.addPage(); y = 16 }
          doc.setFontSize(8)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(...C.muted)
          doc.text('NOTE', ml, y)
          y += 4
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(...C.text)
          const lines = doc.splitTextToSize(sale.memo, cw)
          doc.text(lines, ml, y)
        }

        // ========================================
        // RECEIPT MESSAGE (store thank-you message)
        // ========================================
        const receiptMsg = sale.store && sale.store.receipt_message
        if (receiptMsg) {
          y += 12
          if (y > 265) { doc.addPage(); y = 16 }

          // Centered italic thank-you message
          doc.setFontSize(9)
          doc.setFont('helvetica', 'bolditalic')
          doc.setTextColor(...C.primary)
          const msgLines = doc.splitTextToSize(receiptMsg, cw - 20)
          msgLines.forEach(line => {
            doc.text(line, 105, y, { align: 'center' })
            y += 5
          })
        }

        // ========================================
        // FOOTER (all pages)
        // ========================================
        const totalPages = doc.internal.getNumberOfPages()
        for (let p = 1; p <= totalPages; p++) {
          doc.setPage(p)

          // Footer line
          hline(284, C.border, 0.3)

          // Left: KES LAKAY
          doc.setFontSize(7)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(...C.primary)
          doc.text('KES LAKAY', ml, 289)

          // Center: page
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(...C.muted)
          doc.text(`Page ${p} / ${totalPages}`, 105, 289, { align: 'center' })

          // Right: date
          const now = new Date()
          doc.text(
            `Généré le ${now.toLocaleDateString('fr-FR')} à ${now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`,
            mr, 289, { align: 'right' }
          )
        }

        // Save
        doc.save(`${this.filename}_${sale.sale_number}_${new Date().toISOString().split('T')[0]}.pdf`)
      } catch (error) {
        console.error('PDF generation error:', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.export-pdf-btn {
  min-width: auto !important;
  width: auto !important;
}
</style>
